import { InputField } from '@/components/form/InputField';
import PasswordField from '@/components/form/PasswordField';
import { CenteredLayout } from '@/components/layouts/CenteredLayout';
import { Loadable } from '@/components/Loadable';
import { Button } from '@/components/primitives/Button';
import { CardContent, CardPrimitiveRoot } from '@/components/primitives/Card';
import { Form } from '@/components/primitives/Form';
import { Typography } from '@/components/primitives/Typography';
import { withAuthGuard } from '@/hoc/withAuthGuard';
import { useAuth } from '@/hooks/useAuth';
import { useBackgroundTheme } from '@/hooks/useBackgroundTheme';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useTranslation } from '@/i18n';
import { TransText } from '@/i18n/trans/text';
import { breakpoints } from '@/utils/breakpoints';
import { cn } from '@/utils/cn';
import { isLightBackground } from '@/utils/color';
import { createLoginSchema, type LoginValues } from '@/utils/zodSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FC, useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

interface LoginProps {}

const LoginComponent: FC<LoginProps> = () => {
  const backgroundTheme = useBackgroundTheme();
  const isLight = isLightBackground(backgroundTheme);
  const { t } = useTranslation();
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });
  const [loading, setLoading] = useState(false);
  const { loginWithPassword } = useAuth();
  const navigate = useNavigate();

  usePageTitle('logIn');

  const form = useForm<LoginValues>({
    mode: 'onBlur',
    resolver: zodResolver(createLoginSchema(t)),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = useCallback(
    async (values: LoginValues) => {
      setLoading(true);
      try {
        await loginWithPassword(values);
        navigate('/purchase', { replace: true });
      } catch (error) {
        toast.error(
          <Typography variant="body1">
            <TransText i18nKey="loginFailed" />
            {error instanceof Error ? `: ${error.message}` : ''}
          </Typography>
        );
      } finally {
        setLoading(false);
      }
    },
    [loginWithPassword, navigate]
  );

  return (
    <CenteredLayout>
      <div className="mx-auto flex w-full max-w-[410px] flex-col gap-8 p-4 tablet:my-auto laptop:p-0">
        <Typography variant={isLaptopOrBigger ? 'title' : 'mobile-title'}>
          <TransText i18nKey="logIn" />
        </Typography>
        <Loadable loading={loading} overlay>
          <CardPrimitiveRoot className="rounded-xl">
            <CardContent className="flex flex-col gap-4">
              <Form
                form={form}
                onSubmit={form.handleSubmit(onSubmit)}
                className={cn('flex flex-col gap-3 [&_input]:h-12', {
                  '[&_input]:h-8': isLaptopOrBigger,
                })}
              >
                <InputField
                  name="email"
                  required
                  placeholder={t('text.enterYourEmailAddress')}
                  label={<TransText i18nKey="email" />}
                />
                <PasswordField
                  name="password"
                  required
                  placeholder={t('text.enterYourPassword')}
                  label={<TransText i18nKey="password" />}
                />
                <Button
                  data-testid="login-submit-button"
                  type="submit"
                  className="mt-3"
                  size="medium"
                  fullWidth
                >
                  <TransText i18nKey="logIn" />
                </Button>
              </Form>
              <Button
                data-testid="forgot-password-button"
                onClick={() => navigate('/reset-password')}
                variant="ghost"
                className="self-center"
              >
                <TransText i18nKey="forgotPassword" />
                &#63;
              </Button>
            </CardContent>
          </CardPrimitiveRoot>
        </Loadable>
        <div className="flex items-center gap-3 self-center">
          <Typography variant="body1">
            <TransText i18nKey="notRegisteredYet" />
          </Typography>
          <Button
            variant="ghost"
            data-testid="create-an-account-button"
            className={cn('p-0', isLight ? 'text-primary' : 'text-accent')}
            onClick={() => navigate('/signup')}
          >
            <TransText i18nKey="createAnAccount" />
          </Button>
        </div>
      </div>
    </CenteredLayout>
  );
};

const Login = withAuthGuard('loginOrSignup')(LoginComponent);

export default Login;
